import { useEffect } from 'react';
import type { UseFormReturn } from 'react-hook-form';
import { Controller, useWatch } from 'react-hook-form';
import { winsorizationFrontendConfig } from '@gonfalon/dogfood-flags/dogfood-flags';
import { Message } from '@gonfalon/messages/Message';
import { msg } from '@gonfalon/messages/msg';
import { isLDHosted } from '@gonfalon/metrics/isLDHosted';
import { getMetricDataSourcesQuery } from '@gonfalon/rest-api/queries/metricDataSource';
import { useProjectKey } from '@gonfalon/router/useProjectKey';
import { HelpTooltip } from '@gonfalon/ui/HelpTooltip';
import { Checkbox, FieldError, FieldGroup, Input, Label, NumberField } from '@launchpad-ui/components';
import { useQuery } from '@tanstack/react-query';

import type { MetricForm } from '../formUtils';

import styles from './WinsorizationFields.module.css';

/** Precision for winsorization percentile inputs (5 decimal places). */
const WINSORIZATION_PERCENTILE_SIGFIG = 0.00001;

function validatePercentileBound(
  boundType: 'Lower' | 'Upper',
  bound?: number,
  otherBound?: number,
): string | undefined {
  if (bound === undefined || Number.isNaN(bound)) {
    return msg('metrics.winsorization.boundRequired', { bound: boundType });
  }
  if (bound < 0) {
    return msg('metrics.winsorization.boundBelowZero', { bound: boundType });
  }
  if (bound > 100) {
    return msg('metrics.winsorization.boundAboveHundred', { bound: boundType });
  }
  if (otherBound !== undefined && !Number.isNaN(otherBound)) {
    if (boundType === 'Lower' && bound >= otherBound) {
      return msg('metrics.winsorization.lowerAboveUpper');
    }
    if (boundType === 'Upper' && bound <= otherBound) {
      return msg('metrics.winsorization.upperBelowLower');
    }
  }
  return undefined;
}

export { validatePercentileBound };

function getLowerPercentileError(lower?: number, upper?: number): string | undefined {
  return validatePercentileBound('Lower', lower, upper);
}

function getUpperPercentileError(upper?: number, lower?: number): string | undefined {
  return validatePercentileBound('Upper', upper, lower);
}

export function useWinsorizationEnabled(dataSourceKey?: string): boolean {
  const projectKey = useProjectKey();
  const { data: dataSourcesData } = useQuery(getMetricDataSourcesQuery({ projKey: projectKey }));
  const winsorizationConfig = winsorizationFrontendConfig();

  let integrationKey: string | undefined;
  if (isLDHosted(dataSourceKey)) {
    integrationKey = 'launchdarkly';
  } else {
    const dataSource = dataSourcesData?.items?.find((ds) => ds.key === dataSourceKey);
    integrationKey = dataSource?.integrationKey;
  }

  return integrationKey ? winsorizationConfig[integrationKey] === true : false;
}

// ─── WinsorizationBoundsSection ───────────────────────────────────────────

export interface WinsorizationBoundsSectionProps {
  context: UseFormReturn<MetricForm>;
  /** Form field path for the lower percentile value. */
  lowerControlName: string;
  /** Form field path for the upper percentile value. */
  upperControlName: string;
  /** Current watched value of the lower percentile (for cross-field validation). */
  lowerPercentile?: number;
  /** Current watched value of the upper percentile (for cross-field validation). */
  upperPercentile?: number;
  /** Set outside a titled section, where nothing else explains what the bounds do. */
  showHelpText?: boolean;
}

/**
 * Renders the lower/upper percentile bound number inputs.
 * Both inputs are always visible; callers control when the section is shown.
 * Cross-field validation uses the watched percentile props so each Controller
 * can validate against the other field's current value.
 */
export function WinsorizationBoundsSection({
  context,
  lowerControlName,
  upperControlName,
  lowerPercentile,
  upperPercentile,
  showHelpText = false,
}: WinsorizationBoundsSectionProps) {
  const { control } = context;

  return (
    <>
      {showHelpText && (
        <p className={styles.helpText}>
          <Message id="metrics.winsorization.boundsHelp" />
        </p>
      )}
      <div className={styles.inputsRow}>
        <Controller
          control={control}
          name={lowerControlName as never}
          rules={{
            validate: (val) => getLowerPercentileError(val as number | undefined, upperPercentile) ?? true,
          }}
          render={({ field: { onChange, value, ref, ...field }, fieldState }) => (
            <NumberField
              {...field}
              value={(value as number | undefined) ?? NaN}
              onChange={onChange}
              isInvalid={!!fieldState.error}
              minValue={0}
              maxValue={100}
              step={WINSORIZATION_PERCENTILE_SIGFIG}
              formatOptions={{ maximumFractionDigits: 5 }}
              className={styles.percentileInput}
            >
              <Label>
                <Message id="metrics.winsorization.lowerBound" />
              </Label>
              <div className={styles.inputWrapper}>
                <Input ref={ref} />
                <span className={styles.percentSymbol}>%</span>
              </div>
              {fieldState.error && <FieldError>{fieldState.error.message}</FieldError>}
            </NumberField>
          )}
        />

        <Controller
          control={control}
          name={upperControlName as never}
          rules={{
            validate: (val) => getUpperPercentileError(val as number | undefined, lowerPercentile) ?? true,
          }}
          render={({ field: { onChange, value, ref, ...field }, fieldState }) => (
            <NumberField
              {...field}
              value={(value as number | undefined) ?? NaN}
              onChange={onChange}
              isInvalid={!!fieldState.error}
              minValue={0}
              maxValue={100}
              step={WINSORIZATION_PERCENTILE_SIGFIG}
              formatOptions={{ maximumFractionDigits: 5 }}
              className={styles.percentileInput}
            >
              <Label>
                <Message id="metrics.winsorization.upperBound" />
              </Label>
              <div className={styles.inputWrapper}>
                <Input ref={ref} />
                <span className={styles.percentSymbol}>%</span>
              </div>
              {fieldState.error && <FieldError>{fieldState.error.message}</FieldError>}
            </NumberField>
          )}
        />
      </div>
    </>
  );
}

// ─── Winsorization state ──────────────────────────────────────────────────

/**
 * Winsorization's availability and bounds, plus the write that adds or removes them. Call it
 * wherever winsorization can appear: it also clears stale bounds and fills partial ones in.
 */
export function useWinsorization(context: UseFormReturn<MetricForm>, { isActive = true }: { isActive?: boolean } = {}) {
  const { control, setValue, unregister } = context;

  const [measureType, unitAggregationType, dataSourceKey, lowerPercentile, upperPercentile] = useWatch({
    control,
    name: [
      'measureType',
      'unitAggregationType',
      'dataSourceKey',
      'winsorization.lowerPercentile',
      'winsorization.upperPercentile',
    ],
  });

  const isEnabled = lowerPercentile !== undefined || upperPercentile !== undefined;
  const isWinsorizationEnabledForDataSource = useWinsorizationEnabled(dataSourceKey);
  const isWinsorizationEligible =
    (measureType === 'count' || measureType === 'value') && unitAggregationType !== 'count_distinct';
  const showWinsorization = isActive && isWinsorizationEnabledForDataSource && isWinsorizationEligible;

  // Clear winsorization form state when the section becomes hidden
  useEffect(() => {
    if (isActive && !showWinsorization) {
      unregister(['winsorization.lowerPercentile', 'winsorization.upperPercentile'], {
        keepError: false,
        keepDirty: false,
        keepTouched: false,
      });
    }
  }, [isActive, showWinsorization, unregister]);

  // Populate missing bounds with defaults when editing a metric with partial winsorization
  useEffect(() => {
    if (isEnabled && showWinsorization) {
      if (lowerPercentile === undefined) {
        setValue('winsorization.lowerPercentile', 0, { shouldDirty: false });
      }
      if (upperPercentile === undefined) {
        setValue('winsorization.upperPercentile', 100, { shouldDirty: false });
      }
    }
  }, [isEnabled, showWinsorization, lowerPercentile, upperPercentile, setValue]);

  return {
    isAvailable: showWinsorization,
    isEnabled,
    lowerPercentile,
    upperPercentile,
    setEnabled: (isSelected: boolean) => {
      if (isSelected) {
        setValue('winsorization.lowerPercentile', 0, { shouldDirty: true });
        setValue('winsorization.upperPercentile', 100, { shouldDirty: true });
      } else {
        unregister(['winsorization.lowerPercentile', 'winsorization.upperPercentile'], {
          keepError: false,
          keepDirty: false,
          keepTouched: false,
        });
      }
    },
  };
}

/** The metric's winsorization bounds, wired to the top-level form paths. */
export const WinsorizationBoundFields: React.FC<{
  context: UseFormReturn<MetricForm>;
  lowerPercentile?: number;
  upperPercentile?: number;
  showHelpText?: boolean;
}> = ({ context, lowerPercentile, upperPercentile, showHelpText }) => (
  <WinsorizationBoundsSection
    context={context}
    lowerControlName="winsorization.lowerPercentile"
    upperControlName="winsorization.upperPercentile"
    lowerPercentile={lowerPercentile}
    upperPercentile={upperPercentile}
    showHelpText={showHelpText}
  />
);

/** Winsorization as a checkbox with its bounds beneath: the layout the edit modal uses. */
export const WinsorizationFields: React.FC<{ context: UseFormReturn<MetricForm> }> = ({ context }) => {
  const { isAvailable, isEnabled, lowerPercentile, upperPercentile, setEnabled } = useWinsorization(context);

  if (!isAvailable) {
    return null;
  }

  return (
    <FieldGroup className={styles.winsorizationSection}>
      <div className={styles.labelRow}>
        <Checkbox isSelected={isEnabled} onChange={setEnabled}>
          {msg('metrics.winsorization.enableLabel')}
        </Checkbox>
        <HelpTooltip placement="right">
          <Message id="metrics.winsorization.description" />
        </HelpTooltip>
      </div>
      {isEnabled && (
        <div className={styles.winsorizationContent}>
          <WinsorizationBoundFields
            context={context}
            lowerPercentile={lowerPercentile}
            upperPercentile={upperPercentile}
            showHelpText
          />
        </div>
      )}
    </FieldGroup>
  );
};
